import React from 'react'
import { useCurrentFrame, useVideoConfig, interpolate } from 'remotion'
import { COLORS, FONTS, GLOW, GLASS } from '../shared/theme'
import { fadeIn, entrance, staggerDelay, pulse, SPRINGS } from '../shared/animations'
import SceneWrapper from '../shared/SceneWrapper'

/**
 * MapRoute — Mapa SVG estilizado con ruta animada entre puntos.
 *
 * params: { start, end, waypoints: [str], label }
 */
export default function MapRoute({ params = {}, artDirection = {} }) {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const { start = 'Origen', end = 'Destino', waypoints = [], label = '' } = params

  const allPoints = [start, ...waypoints, end]
  const titleOpacity = fadeIn(frame, 0, 20)
  const mapOpacity = fadeIn(frame, 5, 25)

  const positions = allPoints.map((_, i) => ({
    x: 15 + (i / (allPoints.length - 1 || 1)) * 70,
    y: 35 + Math.sin((i / (allPoints.length - 1 || 1)) * Math.PI) * 20,
  }))

  const totalSegments = positions.length - 1
  const lineProgress = interpolate(frame, [10, 60], [0, totalSegments], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })

  const glowPulse = pulse(frame, 0.05, 0.5, 1.0)

  return (
    <SceneWrapper accent={COLORS.accent.blue} texture={artDirection?.texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 32,
          padding: '60px 100px',
        }}
      >
        {/* Label de ruta */}
        <div
          style={{
            opacity: titleOpacity,
            fontFamily: FONTS.heading,
            fontSize: 28,
            fontWeight: 600,
            color: COLORS.text.muted,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            textAlign: 'center',
          }}
        >
          {label || `${start} → ${end}`}
        </div>

        {/* Mapa SVG */}
        <div
          style={{
            opacity: mapOpacity,
            width: '100%',
            maxWidth: 1400,
            ...GLASS.card,
            borderRadius: 24,
            padding: '40px 60px',
            position: 'relative',
          }}
        >
          <svg
            viewBox="0 0 100 60"
            style={{ width: '100%', height: 'auto', overflow: 'visible' }}
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Glow filter for route */}
            <defs>
              <filter id="routeGlow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="0.8" />
              </filter>
            </defs>

            {/* Grid decorativo de fondo */}
            {[10, 20, 30, 40, 50, 60, 70, 80, 90].map(x => (
              <line
                key={`vg-${x}`}
                x1={x}
                y1={0}
                x2={x}
                y2={60}
                stroke={COLORS.border}
                strokeWidth={0.3}
              />
            ))}
            {[10, 20, 30, 40, 50].map(y => (
              <line
                key={`hg-${y}`}
                x1={0}
                y1={y}
                x2={100}
                y2={y}
                stroke={COLORS.border}
                strokeWidth={0.3}
              />
            ))}

            {/* Glow layer of route (behind) */}
            {positions.slice(0, -1).map((pos, i) => {
              const next = positions[i + 1]
              const segProgress = Math.min(Math.max(lineProgress - i, 0), 1)
              const endX = pos.x + (next.x - pos.x) * segProgress
              const endY = pos.y + (next.y - pos.y) * segProgress
              return (
                <line
                  key={`glow-${i}`}
                  x1={pos.x}
                  y1={pos.y}
                  x2={endX}
                  y2={endY}
                  stroke={COLORS.accent.blue}
                  strokeWidth={3}
                  strokeLinecap="round"
                  filter="url(#routeGlow)"
                  opacity={0.4 * glowPulse}
                />
              )
            })}

            {/* Lineas de ruta animadas */}
            {positions.slice(0, -1).map((pos, i) => {
              const next = positions[i + 1]
              const segProgress = Math.min(Math.max(lineProgress - i, 0), 1)
              const endX = pos.x + (next.x - pos.x) * segProgress
              const endY = pos.y + (next.y - pos.y) * segProgress
              return (
                <line
                  key={`route-${i}`}
                  x1={pos.x}
                  y1={pos.y}
                  x2={endX}
                  y2={endY}
                  stroke={COLORS.accent.blue}
                  strokeWidth={0.8}
                  strokeLinecap="round"
                  strokeDasharray="2 1"
                  strokeDashoffset={-frame * 0.15}
                />
              )
            })}

            {/* Puntos en el mapa */}
            {positions.map((pos, i) => {
              const delay = staggerDelay(i, 8) + 8
              const ptOpacity = fadeIn(frame, delay, 12)
              const ptScale = entrance(frame, fps, delay, SPRINGS.bouncy)
              const isFirst = i === 0
              const isLast = i === positions.length - 1
              const ptColor = isFirst
                ? COLORS.accent.green
                : isLast
                  ? COLORS.accent.red
                  : COLORS.accent.blue

              const ptPulse = isFirst || isLast ? pulse(frame, 0.07, 0.7, 1.0) : 1

              return (
                <g key={i} opacity={ptOpacity}>
                  {/* Glow ring */}
                  {(isFirst || isLast) && (
                    <circle
                      cx={pos.x}
                      cy={pos.y}
                      r={4 * ptScale}
                      fill="none"
                      stroke={ptColor}
                      strokeWidth={0.3}
                      opacity={0.3 * ptPulse}
                    />
                  )}
                  {/* Anillo exterior */}
                  <circle
                    cx={pos.x}
                    cy={pos.y}
                    r={2.5 * ptScale}
                    fill="none"
                    stroke={ptColor}
                    strokeWidth={0.4}
                    opacity={0.5}
                  />
                  {/* Punto central */}
                  <circle cx={pos.x} cy={pos.y} r={1.2 * ptScale} fill={ptColor} />
                </g>
              )
            })}
          </svg>
        </div>

        {/* Nombres de los puntos */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 24,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          {allPoints.map((point, i) => {
            const delay = staggerDelay(i, 5) + 20
            const ptOpacity = fadeIn(frame, delay, 12)
            const isFirst = i === 0
            const isLast = i === allPoints.length - 1
            const ptColor = isFirst
              ? COLORS.accent.green
              : isLast
                ? COLORS.accent.red
                : COLORS.accent.blue

            return (
              <React.Fragment key={i}>
                {i > 0 && (
                  <span style={{ color: COLORS.text.dim, fontSize: 24, opacity: ptOpacity }}>
                    →
                  </span>
                )}
                <div
                  style={{
                    opacity: ptOpacity,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: ptColor,
                      flexShrink: 0,
                      boxShadow: GLOW.sm(ptColor),
                    }}
                  />
                  <span
                    style={{
                      fontFamily: FONTS.heading,
                      fontSize: 22,
                      fontWeight: isFirst || isLast ? 700 : 500,
                      color: isFirst || isLast ? COLORS.text.primary : COLORS.text.secondary,
                    }}
                  >
                    {point}
                  </span>
                </div>
              </React.Fragment>
            )
          })}
        </div>
      </div>
    </SceneWrapper>
  )
}
